// ===================================================================
// KAIXO - useAreaAccess hook
// ===================================================================
// Hook para componentes del panel admin que necesitan estar
// "desbloqueados" con la contraseña de su área. Devuelve:
//   - unlocked: bool — hay token válido en sessionStorage
//   - unlock(password) — verifica contra el backend
//   - error, loading, attemptsLeft, lockedUntil
// ===================================================================

import { useCallback, useEffect, useState } from 'react';
import { adminUnlockArea, getAreaToken, clearAreaToken } from '../../services/adminApi';

export default function useAreaAccess(area, { skipUnlock = false } = {}) {
    // skipUnlock=true cuando el usuario es admin_general — no necesita
    // pasar por modal porque su acceso es directo. Aún así pedimos
    // el token al backend para que las llamadas posteriores lleven
    // el header X-Admin-Area por consistencia.
    const [unlocked, setUnlocked] = useState(() => !!getAreaToken(area));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [attemptsLeft, setAttemptsLeft] = useState(null);
    const [lockedUntil, setLockedUntil] = useState(null);

    // Si es admin_general, intentamos auto-unlock con password vacío
    // (el backend devuelve token para él sin checkear).
    useEffect(() => {
        if (skipUnlock && !unlocked) {
            (async () => {
                try {
                    await adminUnlockArea(area, ''); // backend lo deja pasar por ser general
                    setUnlocked(true);
                } catch {
                    // Si por algún motivo falla, dejamos que el modal pida pwd
                }
            })();
        }
    }, [skipUnlock, area, unlocked]);

    const unlock = useCallback(async (password) => {
        setError('');
        setLoading(true);
        try {
            const data = await adminUnlockArea(area, password);
            if (data?.ok) {
                setUnlocked(true);
                setAttemptsLeft(null);
                setLockedUntil(null);
                return true;
            }
            setError('Contraseña incorrecta');
            return false;
        } catch (err) {
            const msg = err?.response?.data?.error || err.message;
            setError(msg);
            const a = err?.response?.data?.attemptsLeft;
            if (typeof a === 'number') setAttemptsLeft(a);
            const lu = err?.response?.data?.lockedUntil;
            if (lu) setLockedUntil(new Date(lu));
            return false;
        } finally {
            setLoading(false);
        }
    }, [area]);

    const lock = useCallback(() => {
        clearAreaToken(area);
        setUnlocked(false);
    }, [area]);

    return { unlocked, unlock, lock, loading, error, attemptsLeft, lockedUntil };
}
